/* eslint-disable max-lines */
import { RadioGroupChoice } from "../../../components/content-blocks/forms/customer-form-types";
import {
  ADDRESS_FINDER_KEY,
  WHAKAPAPA_KEY,
} from "../../../components/survey-widgets/keys";
import { getTamarikiList } from "../../../config/forms/register/tamariki";
import { PEN_COLOUR } from "../constants";
import { aeChoices, choices } from "../config-generics/aeKao";

const tenant = process.env.ORGANISATION_NAME;

export async function createSurveyConfig({
  tupunaChoices,
}: {
  tupunaChoices: RadioGroupChoice[];
}) {
  const tamarikiList = getTamarikiList();
  return {
    pages: [
      {
        name: "intro",
        title: `${tenant} Registration`,
        elements: [
          {
            type: "html",
            html: `
    <p>
      Tēnā koe, nau mai, haere mai ki te pae rēhita o Rangitāne o Wairau.
    </p>
    <p>
      Te Rūnanga a Rangitāne o Wairau Trust is the representative body for the
      South Island branch of Rangitāne Iwi. We warmly welcome all who whakapapa
      to our tūpuna and invite you to join our whānau by completing the
      registration process.
    </p>
    <p>
      To ensure the integrity of our whakapapa records, we kindly ask that you
      provide accurate and complete information in all sections of the
      application. Each submission will be carefully reviewed by our Whakapapa
      Kōmiti.
    </p>
    <p>
      Once your application has been validated, you will receive a confirmation
      letter along with your unique membership registration number, affirming
      your connection to our iwi. If your application is declined, you may
      choose to dispute the decision.
    </p>
    <p>
      Applicants must whakapapa to at least one tūpuna on the Rangitāne o Wairau
      Primary Ancestors List.
    </p>
    <p>
      If you prefer to complete your registration manually, you may download a
      printable application form
      <a
        href="/docs/20250815 Iwi Registration Form.pdf"
        target="_blank">
        here
      </a>.
    </p>
    <p>
      We look forward to welcoming you and strengthening our connections together.
    </p>
    <p>
      Nāku iti noa, nā Te Rūnanga a Rangitāne o Wairau Trust
    </p>`,
          },
        ],
      },
      {
        name: "personalDetails",
        title: "Personal Details - Taipitopito Matawhaiaro",
        questions: [
          {
            type: "dropdown",
            name: "title",
            title: "Title",
            choices: ["Mrs", "Mr", "Miss", "Ms", "Mx", "Dr"],
          },
          {
            type: "radiogroup",
            name: "gender",
            title: "Gender",
            choices: ["Wahine", "Tāne", "Ira Weherua-kore"],
            colCount: 3,
            startWithNewLine: false,
            isRequired: true,
          },
          {
            type: "radiogroup",
            name: "whangai",
            title: "Whāngai",
            choices,
            startWithNewLine: false,
            isRequired: true,
          },
          {
            type: "text",
            name: "firstName",
            title: "First Name",
            placeHolder: "Ingoa",
            isRequired: true,
          },
          {
            type: "text",
            name: "middleNames",
            title: "Middle Name(s)",
            placeHolder: "Ingoa Waenganui",
            startWithNewLine: false,
          },
          {
            type: "text",
            name: "surname",
            title: "Surname",
            placeHolder: "Ingoa Whānau",
            startWithNewLine: false,
            isRequired: true,
          },
          {
            type: "text",
            inputType: "date",
            name: "dob",
            title: "Birthdate",
            startWithNewLine: true,
            isRequired: true,
            validators: [
              {
                type: "expression",
                expression: "age({dob}) > 17",
                text: "You must be 18 or older",
              },
            ],
          },
          {
            type: "text",
            name: "birthplace",
            title: "Birthplace",
            placeHolder: "Wāhi Whānau",
            isRequired: true,
            startWithNewLine: false,
          },
        ],
      },
      {
        name: "address",
        title: "Address - Wāhi Noho",
        questions: [
          {
            title: "Address",
            type: ADDRESS_FINDER_KEY,
            name: "addressfinder",
            isRequired: true,
          },
        ],
      },
      {
        name: "contactDetails",
        title: "Contact Details - Taipitopito Whakapā",
        questions: [
          {
            title: "Mobile",
            type: "text",
            name: "cell",
            placeHolder: "Waea Pūkoro",
            startWithNewLine: false,
            isRequired: true,
          },
          {
            title: "Phone",
            type: "text",
            name: "phone",
            placeHolder: "Waea",
            startWithNewLine: false,
          },
          {
            title: "Email",
            type: "text",
            name: "email",
            placeHolder: "Īmeera",
            validators: [
              {
                type: "email",
              },
            ],
            startWithNewLine: true,
            isRequired: true,
          },
        ],
      },
      {
        name: "tamariki",
        title: "Children - Tamariki",
        questions: tamarikiList,
      },
      {
        name: "occupation",
        title: "Occupation and Education - Mahi me te Mātauranga",
        questions: [
          {
            type: "dropdown",
            name: "education",
            title: "Education",
            choices: [
              "Apprenticeship",
              "Tertiary",
              "Degree",
              "Masters",
              "Trade",
              "Other",
            ],
          },
          {
            type: "text",
            name: "educationDetails",
            title: "Education Details",
            placeHolder: "Taipitopito",
            startWithNewLine: false,
          },
          {
            type: "text",
            name: "occupation",
            title: "Occupation",
            placeHolder: "Mahi",
            startWithNewLine: true,
          },
        ],
      },
      {
        name: "whakapapa",
        title: "Your Whakapapa",
        questions: [
          {
            name: "tupuna",
            title: "Tupuna",
            type: "dropdown",
            isRequired: true,
            choices: tupunaChoices,
          },
          {
            title: `Include both parents and (only) your line of descent from your ${tenant} Tupuna.`,
            type: WHAKAPAPA_KEY,
            name: "whakapapa",
            isRequired: true,
          },
        ],
      },
      {
        name: "data",
        title: "Data Access and Amendment - Tiaki Raraunga",
        questions: [
          {
            name: "dataAccess",
            title: `In accordance with Principle 6
              of the Privacy Act 2020, Rangitāne shall, upon request by a
              member, ensure the prompt and secure provision of any personal
              information held or processed that relates to that member. The
              member shall also have the right to request correction or
              amendment of their personal information at any time, and Rangitāne
              shall take reasonable steps to ensure that such updates are made
              accurately and without undue delay. Rangitāne shall maintain
              appropriate systems and procedures to facilitate access and
              amendment, and shall not withhold or delay the provision or
              correction of personal information without valid justification.`,
            type: "checkbox",
            choices: aeChoices,
            isRequired: true,
          },
        ],
      },
      {
        name: "privacy",
        title: "Declaration - Tauākī",
        questions: [
          {
            name: "declaration",
            title:
              "I hereby declare that the information in this application is true and correct.",
            type: "checkbox",
            choices: aeChoices,
            isRequired: true,
          },
          {
            name: "signed",
            type: "signaturepad",
            title: "Please sign your name on the signature pad",
            penColor: PEN_COLOUR,
            width: 359,
            isRequired: true,
          },
        ],
      },
    ],
  };
}
